/**
 * GetCare 360 — Centralized Message Registry
 *
 * Maps backend error codes and common messages to professional,
 * healthcare-friendly notifications with:
 *   - Human-friendly title
 *   - Clear reason
 *   - Suggested action
 *   - Severity level
 *
 * Presentation layer only — no business logic.
 */

export const MESSAGE_REGISTRY = {
  // ─── Service Items ─────────────────────────────
  FUNDING_INACTIVE: {
    title: 'Unable to add service item',
    message: 'The selected funding plan is inactive.',
    action: 'Please select an active funding plan.',
    severity: 'warning',
  },
  SERVICE_NOT_FOUND: {
    title: 'Service not found',
    message: 'The selected service could not be located.',
    action: 'Please refresh and try again.',
    severity: 'warning',
  },
  FUNDING_PLAN_NOT_FOUND: {
    title: 'Funding plan not found',
    message: 'The selected funding plan could not be located.',
    action: 'Please select a different funding plan.',
    severity: 'warning',
  },
  PARTICIPANT_NOT_FOUND: {
    title: 'Participant not found',
    message: 'The participant record could not be located.',
    action: 'Please refresh the page or check the participant list.',
    severity: 'warning',
  },
  SERVICE_ITEM_CREATE_FAILED: {
    title: 'Unable to create service item',
    message: 'The service item could not be saved.',
    action: 'Please check the form and try again.',
    severity: 'error',
  },
  GENERATE_SHIFTS_FAILED: {
    title: 'Shift generation failed',
    message: 'Shifts could not be generated for this service item.',
    action: 'Please check the service schedule and try again.',
    severity: 'error',
  },

  // ─── Funding ───────────────────────────────────
  FUNDING_SAVE_FAILED: {
    title: 'Unable to save funding plan',
    message: 'The funding plan could not be saved.',
    action: 'Please check the details and try again.',
    severity: 'error',
  },
  FUNDING_INSUFFICIENT: {
    title: 'Insufficient funding',
    message: 'The funding balance is not sufficient for this operation.',
    action: 'Please review the funding allocation.',
    severity: 'warning',
  },
  FUNDING_EXPIRED: {
    title: 'Funding plan expired',
    message: 'The selected funding plan has expired.',
    action: 'Please select a current funding plan.',
    severity: 'warning',
  },

  // ─── Participants ──────────────────────────────
  PARTICIPANT_CREATE_FAILED: {
    title: 'Unable to create participant',
    message: 'The participant record could not be created.',
    action: 'Please check the required fields and try again.',
    severity: 'error',
  },
  PARTICIPANT_UPDATE_FAILED: {
    title: 'Unable to update participant',
    message: 'The participant record could not be updated.',
    action: 'Please try again.',
    severity: 'error',
  },
  PARTICIPANT_LOAD_FAILED: {
    title: 'Unable to load participant',
    message: 'The participant details could not be loaded.',
    action: 'Please refresh the page.',
    severity: 'error',
  },

  // ─── Rostering ─────────────────────────────────
  STAFF_UNAVAILABLE: {
    title: 'Shift could not be assigned',
    message: 'The selected staff member is unavailable during this time.',
    action: 'Please choose another available staff member.',
    severity: 'warning',
  },
  SHIFT_CONFLICT: {
    title: 'Shift conflict detected',
    message: 'This shift overlaps with an existing assignment.',
    action: 'Please adjust the time or select a different staff member.',
    severity: 'warning',
  },
  SHIFT_NOT_FOUND: {
    title: 'Shift not found',
    message: 'The requested shift could not be located.',
    action: 'It may have been removed. Please refresh.',
    severity: 'warning',
  },
  SHIFT_ASSIGN_FAILED: {
    title: 'Unable to assign shift',
    message: 'The shift could not be assigned.',
    action: 'Please check staff availability and try again.',
    severity: 'error',
  },
  CHECKIN_FAILED: {
    title: 'Check-in unsuccessful',
    message: 'The clock-in could not be recorded.',
    action: 'Please try again or contact your coordinator.',
    severity: 'error',
  },
  CHECKOUT_FAILED: {
    title: 'Check-out unsuccessful',
    message: 'The clock-out could not be recorded.',
    action: 'Please try again or contact your coordinator.',
    severity: 'error',
  },

  // ─── Timesheets ────────────────────────────────
  TIMESHEET_SUBMIT_FAILED: {
    title: 'Timesheet submission failed',
    message: 'The timesheet could not be submitted.',
    action: 'Please check the details and try again.',
    severity: 'error',
  },
  TIMESHEET_APPROVAL_FAILED: {
    title: 'Approval unsuccessful',
    message: 'The timesheet could not be approved.',
    action: 'Please review the timesheet and try again.',
    severity: 'error',
  },
  TIMESHEET_LOAD_FAILED: {
    title: 'Unable to load timesheets',
    message: 'The timesheet data could not be loaded.',
    action: 'Please refresh the page.',
    severity: 'error',
  },
  TIMESHEET_EDIT_FAILED: {
    title: 'Unable to edit timesheet',
    message: 'The timesheet could not be updated.',
    action: 'Please check the details and try again.',
    severity: 'error',
  },
  TIMESHEET_REAPPROVAL_FAILED: {
    title: 'Reapproval unsuccessful',
    message: 'The timesheet could not be reapproved.',
    action: 'Please review and try again.',
    severity: 'error',
  },
  VALIDATION_ADJUSTED_HOURS: {
    title: 'Invalid adjusted hours',
    message: 'Please enter a valid number for adjusted hours.',
    action: null,
    severity: 'warning',
  },
  VALIDATION_REJECTION_REASON: {
    title: 'Reason required',
    message: 'A rejection reason is required.',
    action: null,
    severity: 'warning',
  },
  VALIDATION_CORRECTION_NOTES: {
    title: 'Notes required',
    message: 'Correction notes are required.',
    action: null,
    severity: 'warning',
  },
  VALIDATION_EDIT_REASON: {
    title: 'Reason too short',
    message: 'Edit reason must be at least 10 characters.',
    action: null,
    severity: 'warning',
  },

  // ─── Permissions ───────────────────────────────
  PERMISSION_DENIED: {
    title: 'Access denied',
    message: 'You do not have permission to perform this action.',
    action: 'Please contact your administrator.',
    severity: 'error',
  },
  MISSING_PERMISSION: {
    title: 'Access denied',
    message: 'You do not have the required permission.',
    action: 'Please contact your administrator.',
    severity: 'error',
  },

  // ─── General ───────────────────────────────────
  NETWORK_ERROR: {
    title: 'Connection issue',
    message: 'Unable to reach the server.',
    action: 'Please check your connection and try again.',
    severity: 'error',
  },
  SERVER_UNAVAILABLE: {
    title: 'System temporarily unavailable',
    message: 'The request could not be completed.',
    action: 'Please try again shortly.',
    severity: 'error',
  },
  EXPORT_FAILED: {
    title: 'Export unsuccessful',
    message: 'The file could not be generated.',
    action: 'Please try again.',
    severity: 'error',
  },
  LOAD_FAILED: {
    title: 'Unable to load data',
    message: 'The requested data could not be loaded.',
    action: 'Please refresh the page.',
    severity: 'error',
  },
  SAVE_FAILED: {
    title: 'Unable to save',
    message: 'Your changes could not be saved.',
    action: 'Please try again.',
    severity: 'error',
  },
  DELETE_FAILED: {
    title: 'Unable to delete',
    message: 'The item could not be removed.',
    action: 'Please try again.',
    severity: 'error',
  },
  VALIDATION_REQUIRED: {
    title: 'Required field missing',
    message: 'Please fill in all required fields.',
    action: null,
    severity: 'warning',
  },

  // ─── Fallback ──────────────────────────────────
  UNKNOWN: {
    title: 'Something needs attention',
    message: 'The request could not be completed.',
    action: 'Please try again or contact your administrator.',
    severity: 'error',
  },

  // ═══════════════════════════════════════════════════
  // PHASE 2 — Payroll, Invoicing, Claims, SIL, Centre
  // ═══════════════════════════════════════════════════

  // ─── Payroll (Admin Dashboard) ──────────────────
  PAYROLL_LOAD_FAILED: {
    title: 'Unable to load payroll',
    message: 'Payroll preview data could not be loaded.',
    action: 'Please refresh the page.',
    severity: 'error',
  },
  PAYROLL_LOCK_FAILED: {
    title: 'Payroll lock action failed',
    message: 'The payroll week could not be updated.',
    action: 'Please review the week status and try again.',
    severity: 'error',
  },
  PAYROLL_UNLOCK_REASON_SHORT: {
    title: 'Reason too short',
    message: 'Unlock reason must be at least 5 characters.',
    action: null,
    severity: 'warning',
  },
  PAYROLL_EXPORT_FAILED: {
    title: 'Payroll export failed',
    message: 'The Xero CSV export could not be generated.',
    action: 'Please check for warnings and try again.',
    severity: 'error',
  },
  PAYROLL_EXPORT_VALIDATION: {
    title: 'Export validation failed',
    message: 'Some entries have issues preventing export.',
    action: 'Please resolve the listed warnings first.',
    severity: 'warning',
  },

  // ─── Payroll Batches ────────────────────────────
  PAYROLL_BATCH_LOAD_FAILED: {
    title: 'Unable to load payroll batches',
    message: 'Payroll batch data could not be loaded.',
    action: 'Please refresh the page.',
    severity: 'error',
  },
  PAYROLL_BATCH_CREATE_FAILED: {
    title: 'Unable to create batch',
    message: 'The payroll batch could not be created.',
    action: 'Please check the period selection and try again.',
    severity: 'error',
  },
  PAYROLL_BATCH_ACTION_FAILED: {
    title: 'Batch action failed',
    message: 'The batch action could not be completed.',
    action: 'Please try again.',
    severity: 'error',
  },
  PAYROLL_BATCH_EXPORT_FAILED: {
    title: 'Batch export failed',
    message: 'The payroll batch export could not be generated.',
    action: 'Please try again.',
    severity: 'error',
  },
  PAYROLL_PERIOD_SELECT_REQUIRED: {
    title: 'Period required',
    message: 'Please select a payroll period before creating a batch.',
    action: null,
    severity: 'warning',
  },

  // ─── Payroll Periods ────────────────────────────
  PAYROLL_PERIOD_LOAD_FAILED: {
    title: 'Unable to load payroll periods',
    message: 'Period data could not be loaded.',
    action: 'Please refresh the page.',
    severity: 'error',
  },
  PAYROLL_PERIOD_CREATE_FAILED: {
    title: 'Unable to create period',
    message: 'The payroll period could not be created.',
    action: 'Please check the dates and try again.',
    severity: 'error',
  },
  PAYROLL_PERIOD_CLOSE_FAILED: {
    title: 'Unable to close period',
    message: 'The payroll period could not be closed.',
    action: 'Please ensure all batches are finalised.',
    severity: 'error',
  },

  // ─── Payroll Exceptions ─────────────────────────
  PAYROLL_EXCEPTIONS_LOAD_FAILED: {
    title: 'Unable to load exceptions',
    message: 'Payroll exception data could not be loaded.',
    action: 'Please refresh the page.',
    severity: 'error',
  },

  // ─── Invoicing ──────────────────────────────────
  INVOICE_LOAD_FAILED: {
    title: 'Unable to load invoices',
    message: 'Invoice data could not be retrieved.',
    action: 'Please refresh the page.',
    severity: 'error',
  },
  INVOICE_GENERATE_FAILED: {
    title: 'Invoice generation failed',
    message: 'The invoice could not be generated.',
    action: 'Please check the service agreement and try again.',
    severity: 'error',
  },
  INVOICE_CSV_EXPORT_FAILED: {
    title: 'CSV export failed',
    message: 'The invoice CSV could not be generated.',
    action: 'Please try again.',
    severity: 'error',
  },
  INVOICE_PDF_EXPORT_FAILED: {
    title: 'PDF export failed',
    message: 'The invoice PDF could not be generated.',
    action: 'Please try again.',
    severity: 'error',
  },
  INVOICE_DETAIL_LOAD_FAILED: {
    title: 'Unable to load invoice',
    message: 'Invoice details could not be retrieved.',
    action: 'Please refresh the page.',
    severity: 'error',
  },
  INVOICE_REGEN_LOAD_FAILED: {
    title: 'Unable to load invoice versions',
    message: 'Invoice version history could not be retrieved.',
    action: 'Please refresh the page.',
    severity: 'error',
  },
  INVOICE_REGEN_FAILED: {
    title: 'Regeneration failed',
    message: 'The invoice could not be regenerated.',
    action: 'Please review the changes and try again.',
    severity: 'error',
  },
  INVOICE_ADJUSTMENT_FAILED: {
    title: 'Adjustment failed',
    message: 'The invoice adjustment could not be approved.',
    action: 'Please review and try again.',
    severity: 'error',
  },
  INVOICE_REGEN_LIST_FAILED: {
    title: 'Unable to load regeneration queue',
    message: 'Invoices needing regeneration could not be loaded.',
    action: 'Please refresh the page.',
    severity: 'error',
  },
  INVOICE_PENDING_ADJUSTMENTS_FAILED: {
    title: 'Unable to load adjustments',
    message: 'Pending adjustments could not be loaded.',
    action: 'Please refresh the page.',
    severity: 'error',
  },

  // ─── Claims ─────────────────────────────────────
  CLAIM_LOAD_FAILED: {
    title: 'Unable to load claim',
    message: 'Claim details could not be retrieved.',
    action: 'Please refresh the page.',
    severity: 'error',
  },
  CLAIM_LIST_LOAD_FAILED: {
    title: 'Unable to load claims',
    message: 'Claims list could not be loaded.',
    action: 'Please refresh the page.',
    severity: 'error',
  },
  CLAIM_GENERATE_FAILED: {
    title: 'Claim generation failed',
    message: 'The NDIS claim could not be generated.',
    action: 'Please check the invoices and try again.',
    severity: 'error',
  },
  CLAIM_ACTION_FAILED: {
    title: 'Claim action failed',
    message: 'The claim action could not be completed.',
    action: 'Please try again.',
    severity: 'error',
  },
  CLAIM_EXPORT_FAILED: {
    title: 'Claim export failed',
    message: 'The claim file could not be exported.',
    action: 'Please try again.',
    severity: 'error',
  },
  CLAIM_VALIDATION_FAILED: {
    title: 'Claim validation failed',
    message: 'Some NDIS fields require attention before export.',
    action: 'Please review the validation errors.',
    severity: 'warning',
  },
  CLAIM_RECONCILE_EMPTY: {
    title: 'No reconciliation changes',
    message: 'No changes were detected for reconciliation.',
    action: 'Please update at least one claim line.',
    severity: 'warning',
  },
  CLAIM_RECONCILE_IMPORT_FAILED: {
    title: 'Import failed',
    message: 'The reconciliation file could not be imported.',
    action: 'Please check the file format and try again.',
    severity: 'error',
  },
  CLAIM_DOWNLOAD_FAILED: {
    title: 'Download failed',
    message: 'The file could not be downloaded.',
    action: 'Please try again.',
    severity: 'error',
  },
  MISSED_CLAIMS_LOAD_FAILED: {
    title: 'Unable to load missed claims',
    message: 'Missed claims data could not be retrieved.',
    action: 'Please refresh the page.',
    severity: 'error',
  },

  // ─── Finance Pipeline ───────────────────────────
  FINANCE_PIPELINE_LOAD_FAILED: {
    title: 'Unable to load finance data',
    message: 'Finance pipeline data could not be loaded.',
    action: 'Please refresh the page.',
    severity: 'error',
  },

  // ─── SIL ────────────────────────────────────────
  SIL_DATA_LOAD_FAILED: {
    title: 'Unable to load SIL data',
    message: 'SIL intelligence data could not be retrieved.',
    action: 'Please refresh the page.',
    severity: 'error',
  },
  SIL_COMPATIBILITY_FAILED: {
    title: 'Compatibility check failed',
    message: 'The compatibility analysis could not be completed.',
    action: 'Please try again.',
    severity: 'error',
  },
  SIL_MATCH_FAILED: {
    title: 'Match search failed',
    message: 'Compatible matches could not be found.',
    action: 'Please try again.',
    severity: 'error',
  },
  SIL_VALIDATION_PARTICIPANT_HOUSE: {
    title: 'Selection required',
    message: 'Please select both a participant and a house.',
    action: null,
    severity: 'warning',
  },
  SIL_VALIDATION_PARTICIPANT: {
    title: 'Participant required',
    message: 'Please select a participant.',
    action: null,
    severity: 'warning',
  },
  SIL_SHIFT_CREATE_FAILED: {
    title: 'Unable to create SIL shift',
    message: 'The SIL roster shift could not be created.',
    action: 'Please check the details and try again.',
    severity: 'error',
  },
  SIL_SHIFT_VALIDATION: {
    title: 'Required fields missing',
    message: 'Please fill in all required shift fields.',
    action: null,
    severity: 'warning',
  },
  SIL_SHIFT_TIME_REQUIRED: {
    title: 'Shift time required',
    message: 'Please select at least one shift time.',
    action: null,
    severity: 'warning',
  },
  SIL_RECURRING_FAILED: {
    title: 'Recurring shifts failed',
    message: 'The recurring SIL shifts could not be created.',
    action: 'Please check the schedule and try again.',
    severity: 'error',
  },
  SIL_HOUSE_LOAD_FAILED: {
    title: 'Unable to load house details',
    message: 'The SIL house data could not be loaded.',
    action: 'Please refresh the page.',
    severity: 'error',
  },
  SIL_ROSTER_LOAD_FAILED: {
    title: 'Unable to load SIL roster',
    message: 'Roster data could not be retrieved.',
    action: 'Please refresh the page.',
    severity: 'error',
  },

  // ─── Centre Sessions ────────────────────────────
  CENTRE_SESSIONS_LOAD_FAILED: {
    title: 'Unable to load sessions',
    message: 'Centre session data could not be loaded.',
    action: 'Please refresh the page.',
    severity: 'error',
  },
  CENTRE_SESSION_DETAIL_FAILED: {
    title: 'Unable to load session',
    message: 'Session details could not be retrieved.',
    action: 'Please refresh the page.',
    severity: 'error',
  },
  CENTRE_SESSION_CREATE_FAILED: {
    title: 'Unable to create session',
    message: 'The centre session could not be created.',
    action: 'Please check the details and try again.',
    severity: 'error',
  },
  CENTRE_SESSION_ACTION_FAILED: {
    title: 'Session action failed',
    message: 'The session could not be updated.',
    action: 'Please try again.',
    severity: 'error',
  },
  CENTRE_PARTICIPANT_ADD_FAILED: {
    title: 'Unable to add participant',
    message: 'The participant could not be added to this session.',
    action: 'Please try again.',
    severity: 'error',
  },
  CENTRE_PARTICIPANT_REMOVE_FAILED: {
    title: 'Unable to remove participant',
    message: 'The participant could not be removed from this session.',
    action: 'Please try again.',
    severity: 'error',
  },
  CENTRE_STAFF_ASSIGN_FAILED: {
    title: 'Unable to assign staff',
    message: 'Staff could not be assigned to this session.',
    action: 'Please try again.',
    severity: 'error',
  },
  CENTRE_STAFF_REMOVE_FAILED: {
    title: 'Unable to remove staff',
    message: 'Staff could not be removed from this session.',
    action: 'Please try again.',
    severity: 'error',
  },
  CENTRE_ACTIVITY_ADD_FAILED: {
    title: 'Unable to add activity',
    message: 'The activity could not be added.',
    action: 'Please try again.',
    severity: 'error',
  },
  CENTRE_ACTIVITY_DELETE_FAILED: {
    title: 'Unable to delete activity',
    message: 'The activity could not be removed.',
    action: 'Please try again.',
    severity: 'error',
  },
  CENTRE_LINK_PARTICIPANTS_FAILED: {
    title: 'Unable to link participants',
    message: 'Participants could not be linked.',
    action: 'Please try again.',
    severity: 'error',
  },
  CENTRE_SHIFTS_CREATE_FAILED: {
    title: 'Unable to create shifts',
    message: 'Centre session shifts could not be generated.',
    action: 'Please check the schedule and try again.',
    severity: 'error',
  },
};

/**
 * Backend error string → registry code mapping.
 * Maps common backend error messages to their registry codes.
 */
export const BACKEND_MESSAGE_MAP = {
  // Phase 1
  'Participant not found': 'PARTICIPANT_NOT_FOUND',
  'Service not found': 'SERVICE_NOT_FOUND',
  'Funding plan not found': 'FUNDING_PLAN_NOT_FOUND',
  'Item not found': 'SERVICE_NOT_FOUND',
  'Service code already exists': 'SERVICE_ITEM_CREATE_FAILED',
  'Agreement not found': 'FUNDING_INACTIVE',
  'Agreement must be ACTIVE': 'FUNDING_INACTIVE',
  'Agreement line item not found': 'FUNDING_INACTIVE',
  'Shift not found': 'SHIFT_NOT_FOUND',
  'Check-in failed': 'CHECKIN_FAILED',
  'Check-out failed': 'CHECKOUT_FAILED',
  // Phase 2 — Invoicing
  'Failed to load invoices': 'INVOICE_LOAD_FAILED',
  'Failed to generate invoice': 'INVOICE_GENERATE_FAILED',
  'Invoice not found': 'INVOICE_DETAIL_LOAD_FAILED',
  // Phase 2 — Claims
  'Failed to load claims': 'CLAIM_LIST_LOAD_FAILED',
  'Failed to load claim': 'CLAIM_LOAD_FAILED',
  'Claim not found': 'CLAIM_LOAD_FAILED',
  'Generation failed': 'CLAIM_GENERATE_FAILED',
  'No reconciliation changes': 'CLAIM_RECONCILE_EMPTY',
  // Phase 2 — SIL
  'Failed to load SIL data': 'SIL_DATA_LOAD_FAILED',
  'Failed to check compatibility': 'SIL_COMPATIBILITY_FAILED',
  'Failed to find matches': 'SIL_MATCH_FAILED',
  // Phase 2 — Centre
  'Failed to load sessions': 'CENTRE_SESSIONS_LOAD_FAILED',
  'Failed to create session': 'CENTRE_SESSION_CREATE_FAILED',
};
